import { RotateDirection } from "@react-pdf-viewer/core"
import { type RenderCurrentPageLabelProps, type pageNavigationPlugin } from "@react-pdf-viewer/page-navigation"
import { type toolbarPlugin } from "@react-pdf-viewer/toolbar"
import { Divider, Dropdown, InputNumber } from "antd"
import React, { useRef } from "react"
import {
    ArrowLeftCircle,
    ArrowRightCircle,
    Download as DownloadIcon,
    Maximize,
    Printer,
    RotateCcw,
    RotateCw,
    Search as SearchIcon,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
} from "react-feather"

import { COLOR } from "@finway-group/shared/lib/consts"
import { CustomSearchMenu } from "~Components/fileViewer/customSearchMenu"
import { useStateIfMounted } from "~Shared/utils/hooks/useStateIfMounted"
import { getPopupAnchor } from "~Shared/utils/popup.utils"

export interface FileViewerToolbarProps {
    isXmlEInvoice?: boolean
    RightSideToolbar?: React.ReactNode | null
    onUnsupportedFormatDownload?: () => void
    pageNavigationPluginInstance: ReturnType<typeof pageNavigationPlugin>
    toolbarPluginInstance: ReturnType<typeof toolbarPlugin>
}

export const FileViewerToolbar = React.memo(
    ({ isXmlEInvoice = false, RightSideToolbar, onUnsupportedFormatDownload, pageNavigationPluginInstance, toolbarPluginInstance }: FileViewerToolbarProps) => {
        const [searchDropdownVisible, setSearchDropdownVisible] = useStateIfMounted(false)
        const anchorRef = useRef(null)

        const toolBarIconColor = "#3F4246" // same as in figma

        const { jumpToPage, CurrentPageLabel } = pageNavigationPluginInstance
        const { Toolbar } = toolbarPluginInstance

        const onChangePage = (value: number | null) => {
            if (typeof value === "number") {
                jumpToPage(value - 1)
            }
        }

        return (
            <div
                style={{
                    alignItems: "center",
                    backgroundColor: COLOR.white,
                    borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                    display: "flex",
                    paddingBottom: "10px",
                    width: "100%",
                }}
            >
                <Toolbar>
                    {(slots) => {
                        const { Download, EnterFullScreen, GoToNextPage, GoToPreviousPage, Print, Zoom, ZoomIn, ZoomOut, Rotate, Search } = slots

                        if (isXmlEInvoice) {
                            return (
                                <div className="flex flex-auto flex-row-reverse mr-20">
                                    {/* Custom download button  */}
                                    <Download>
                                        {(props) => (
                                            <span
                                                onClick={() => {
                                                    if (onUnsupportedFormatDownload) {
                                                        return onUnsupportedFormatDownload()
                                                    }
                                                    return props.onClick()
                                                }}
                                                className="cursor-pointer self-center"
                                            >
                                                <DownloadIcon color={toolBarIconColor} size="18" />
                                            </span>
                                        )}
                                    </Download>

                                    {RightSideToolbar && (
                                        <>
                                            <Divider type="vertical" className="mx-10 self-center" />
                                            {RightSideToolbar}
                                        </>
                                    )}
                                </div>
                            )
                        }

                        return (
                            <div className="flex w-full justify-between">
                                <div className="flex min-w-1/4 flex-auto" ref={anchorRef}>
                                    {/* Custom search box */}
                                    <Search>
                                        {(props) => (
                                            <Dropdown
                                                overlay={() => <CustomSearchMenu props={props} setSearchDropdownVisible={setSearchDropdownVisible} />}
                                                placement="bottomLeft"
                                                open={searchDropdownVisible}
                                                arrow
                                                getPopupContainer={getPopupAnchor(anchorRef.current)}
                                            >
                                                <span className="cursor-pointer self-center mx-5" onClick={() => setSearchDropdownVisible(true)}>
                                                    <SearchIcon color={toolBarIconColor} size="18" />
                                                </span>
                                            </Dropdown>
                                        )}
                                    </Search>

                                    <Divider type="vertical" className="self-center" />

                                    {/* Custom page navigation */}
                                    <GoToPreviousPage>
                                        {(props) => (
                                            <span onClick={props.onClick} className="mx-5 cursor-pointer self-center">
                                                <ArrowLeftCircle color={toolBarIconColor} size="18" />
                                            </span>
                                        )}
                                    </GoToPreviousPage>

                                    <CurrentPageLabel>
                                        {(props: RenderCurrentPageLabelProps) => (
                                            <>
                                                <InputNumber
                                                    className="w-50 self-center"
                                                    size="small"
                                                    min={1}
                                                    max={props.numberOfPages}
                                                    value={props.currentPage + 1}
                                                    onChange={onChangePage}
                                                />
                                                <span className="ml-4 text-sm text-text self-center">{` /${props.numberOfPages}`}</span>
                                            </>
                                        )}
                                    </CurrentPageLabel>

                                    <GoToNextPage>
                                        {(props) => (
                                            <span onClick={props.onClick} className="cursor-pointer ml-10 self-center">
                                                <ArrowRightCircle color={toolBarIconColor} size="18" />
                                            </span>
                                        )}
                                    </GoToNextPage>

                                    <Divider type="vertical" className="mx-10 self-center" />

                                    {/* Custom rotation buttons  */}
                                    <Rotate direction={RotateDirection.Backward}>
                                        {(props) => (
                                            <span onClick={props.onClick} className="mr-5 cursor-pointer self-center">
                                                <RotateCcw color={toolBarIconColor} size="18" />
                                            </span>
                                        )}
                                    </Rotate>
                                    <Rotate direction={RotateDirection.Forward}>
                                        {(props) => (
                                            <span onClick={props.onClick} className="cursor-pointer self-center">
                                                <RotateCw color={toolBarIconColor} size="18" />
                                            </span>
                                        )}
                                    </Rotate>
                                </div>

                                <div className="flex flex-1 justify-center">
                                    <Divider type="vertical" className="mx-10 self-center xs:hidden" />

                                    {/* Custom zoom out button  */}
                                    <ZoomOut>
                                        {(props) => (
                                            <span onClick={props.onClick} className="cursor-pointer self-center">
                                                <ZoomOutIcon color={toolBarIconColor} size="18" />
                                            </span>
                                        )}
                                    </ZoomOut>
                                    <span className="xs:inline-flex hidden">
                                        {" "}
                                        <Zoom></Zoom>
                                    </span>

                                    {/* Custom zoom in button  */}
                                    <ZoomIn>
                                        {(props) => (
                                            <span onClick={props.onClick} className="cursor-pointer self-center">
                                                <ZoomInIcon color={toolBarIconColor} size="18" />
                                            </span>
                                        )}
                                    </ZoomIn>
                                    <Divider type="vertical" className="mx-10 self-center xs:hidden" />
                                </div>

                                <div className="flex  w-1/4 flex-auto flex-row-reverse">
                                    {/* Custom print button  */}
                                    <Print>
                                        {(props) => (
                                            <span onClick={props.onClick} className="cursor-pointer self-center mr-10">
                                                <Printer color={toolBarIconColor} size="18" />
                                            </span>
                                        )}
                                    </Print>

                                    <Divider type="vertical" className="mx-10 self-center" />

                                    {/* Custom download button  */}
                                    <Download>
                                        {(props) => (
                                            <span
                                                onClick={() => {
                                                    if (onUnsupportedFormatDownload) {
                                                        return onUnsupportedFormatDownload()
                                                    }
                                                    return props.onClick()
                                                }}
                                                className="cursor-pointer self-center"
                                            >
                                                <DownloadIcon color={toolBarIconColor} size="18" />
                                            </span>
                                        )}
                                    </Download>

                                    <Divider type="vertical" className="mx-10 self-center" />

                                    {/* Custom full screen button  */}
                                    <EnterFullScreen>
                                        {(props) => (
                                            <span onClick={props.onClick} className="cursor-pointer self-center">
                                                <Maximize color={toolBarIconColor} size="18" />
                                            </span>
                                        )}
                                    </EnterFullScreen>

                                    {RightSideToolbar && (
                                        <>
                                            <Divider type="vertical" className="mx-10 self-center" />
                                            {RightSideToolbar}
                                        </>
                                    )}
                                </div>
                            </div>
                        )
                    }}
                </Toolbar>
            </div>
        )
    },
)
